/**
 * Dostęp do modułów — konto startowe + moduły odblokowywane na stałe.
 *
 * Front NIE liczy dostępu sam: serwer zwraca w `/user/me` (i przy logowaniu) gotową listę
 * `modules` (startowe + odblokowane; admin — wszystkie), wyliczoną z `common::modules`.
 * Tu są tylko identyfikatory, katalog do ekranu odblokowania i jedna funkcja sprawdzająca —
 * używana przez `moduleGuard`, menu boczne i nagłówek, żeby nigdy się nie rozjechały.
 */

/** Identyfikator modułu — te same wartości co w `backend/crates/common/src/modules.rs`. */
export type ModuleId =
	// startowe
	| 'dashboard'
	| 'notifications'
	| 'cryptolist'
	| 'climate'
	| 'advisor'
	// do odblokowania
	| 'sentiment'
	| 'volume'
	| 'consolidations'
	| 'trendlines'
	| 'sr_zones'
	| 'volumetric'
	| 'gains_monitor'
	| 'daily_range'
	| 'market_bottoms'
	| 'overbalance'
	| 'catch_trend'
	| 'oil'
	| 'analytics360'
	| 'btc'
	| 'portfolio'
	| 'exchange';

/** Moduły każdego konta (lustro `STARTER` w backendzie — używane, gdy lista z serwera jeszcze
 *  nie dotarła, np. tuż po starcie aplikacji ze starą sesją). */
export const STARTER_MODULES: readonly ModuleId[] = [
	'dashboard',
	'notifications',
	'cryptolist',
	'climate',
	'advisor',
];

/** Jak można dostać moduł spoza startu (lustro `UNLOCKABLE` w backendzie). */
export const UNLOCKABLE_MODULES: readonly { id: ModuleId; adminOnly?: boolean }[] = [
	{ id: 'sentiment' },
	{ id: 'volume' },
	{ id: 'consolidations' },
	{ id: 'trendlines' },
	{ id: 'sr_zones' },
	{ id: 'volumetric' },
	{ id: 'gains_monitor' },
	{ id: 'daily_range' },
	{ id: 'market_bottoms' },
	{ id: 'overbalance' },
	{ id: 'catch_trend' },
	{ id: 'oil' },
	{ id: 'analytics360' },
	{ id: 'btc' },
	{ id: 'portfolio' },
	// Prawdziwe zlecenia na giełdzie — przyznaje admin, nie są nagrodą za czas w aplikacji.
	{ id: 'exchange', adminOnly: true },
];

export function isKnownModule(m: string | null | undefined): m is ModuleId {
	return (
		!!m &&
		(STARTER_MODULES.includes(m as ModuleId) || UNLOCKABLE_MODULES.some((u) => u.id === m))
	);
}

export function isAdminOnlyModule(m: ModuleId): boolean {
	return UNLOCKABLE_MODULES.some((u) => u.id === m && u.adminOnly);
}

/** Minimalny kształt użytkownika potrzebny do sprawdzenia dostępu. */
export interface ModuleAccessUser {
	role?: string;
	modules?: string[];
}

/**
 * Czy użytkownik ma dostęp do modułu. Brak modułu (`undefined`) = strona ogólna, otwarta dla
 * każdego zalogowanego. Admin ma wszystko.
 */
export function canAccessModule(
	user: ModuleAccessUser | null | undefined,
	module: ModuleId | undefined,
): boolean {
	if (!module || user?.role === 'admin') {
		return true;
	}

	if (STARTER_MODULES.includes(module)) {
		return true;
	}

	return !!user?.modules?.includes(module);
}
